<template>
  <ModalShell :visible="switchKeySyncDialogState.visible" transition-name="fade" :z-index="2100" :interactive="true" :close-on-overlay="false" :blur-page="true">
    <div class="sks-wrap">
      <div class="sks-card" @click.stop>
        <!-- 标题 -->
        <div class="sks-title">{{ $t('switch_key_sync_dialog_title') }}</div>
        <div class="sks-subtitle">{{ $t('switch_key_sync_dialog_subtitle') }}</div>

        <!-- 可视化动画区 -->
        <div class="sks-diagram">
          <!-- 场景A：不同步（顶部，带红色警示） -->
          <div class="diagram-row no-sync">
            <div class="preset-box current">
              <div class="preset-label">{{ $t('switch_key_sync_dialog_current') }}</div>
              <div class="key-chip">
                <SvgAsset :content="sksKeyIconSvg" />
                {{ $t('switch_key_sync_dialog_key_set') }}
              </div>
            </div>

            <div class="arrows-col">
              <div class="arrow-line right-only">
                <svg viewBox="0 0 60 20" class="arrow-svg">
                  <defs>
                    <marker id="ah1" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
                      <path d="M0,0 L6,3 L0,6 Z" fill="#E53E3E" />
                    </marker>
                  </defs>
                  <line x1="4" y1="10" x2="52" y2="10" stroke="#E53E3E" stroke-width="1.5" marker-end="url(#ah1)" stroke-dasharray="5,3" class="dash-anim" />
                </svg>
              </div>
              <div class="blocked-hint">
                <SvgAsset :content="sksBlockedIconSvg" />
                {{ $t('switch_key_sync_dialog_no_way_back') }}
              </div>
            </div>

            <div class="preset-box other locked">
              <div class="preset-label">{{ $t('switch_key_sync_dialog_other') }}</div>
              <div class="key-chip empty">
                <SvgAsset :content="sksEmptyIconSvg" />
                {{ $t('switch_key_sync_dialog_key_missing') }}
              </div>
            </div>
          </div>

          <!-- 场景B：同步后（底部，带绿色） -->
          <div class="diagram-row synced">
            <div class="preset-box current synced-box">
              <div class="preset-label">{{ $t('switch_key_sync_dialog_current') }}</div>
              <div class="key-chip">
                <SvgAsset :content="sksKeyIconSvg" />
                {{ $t('switch_key_sync_dialog_key_set') }}
              </div>
            </div>

            <div class="arrows-col">
              <div class="arrow-line both-ways">
                <svg viewBox="0 0 60 20" class="arrow-svg">
                  <defs>
                    <marker id="ah2" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
                      <path d="M0,0 L6,3 L0,6 Z" fill="#38A169" />
                    </marker>
                    <marker id="ah3" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
                      <path d="M0,0 L6,3 L0,6 Z" fill="#38A169" />
                    </marker>
                  </defs>
                  <line x1="4" y1="7" x2="52" y2="7" stroke="#38A169" stroke-width="1.5" marker-end="url(#ah2)" class="solid-anim" />
                  <line x1="56" y1="18" x2="3" y2="18" stroke="#38A169" stroke-width="1.5" marker-end="url(#ah3)" class="solid-anim-rev" />
                </svg>
              </div>
              <div class="synced-hint">
                <SvgAsset :content="sksSyncIconSvg" />
                {{ $t('switch_key_sync_dialog_can_switch_back') }}
              </div>
            </div>

            <div class="preset-box other synced-box">
              <div class="preset-label">{{ $t('switch_key_sync_dialog_other') }}</div>
              <div class="key-chip synced-chip">
                <SvgAsset :content="sksKeyIconSvg" />
                {{ $t('switch_key_sync_dialog_key_synced') }}
              </div>
            </div>
          </div>
        </div>

        <!-- 操作按钮 -->
        <div class="sks-actions">
          <button class="sks-btn primary" @click="confirm(true)">{{ $t('switch_key_sync_yes') }}</button>
          <button class="sks-btn secondary" @click="confirm(false)">{{ $t('switch_key_sync_no') }}</button>
        </div>
      </div>
    </div>
  </ModalShell>
</template>

<script setup lang="ts">
import ModalShell from '../common/ModalShell.vue'
import SvgAsset from '../common/SvgAsset.vue'
import { switchKeySyncDialogState, closeSwitchKeySyncDialog } from './switch_key_sync_dialog'
import sksKeyIconSvg from '@/assets/inline-svg/sks-key-icon.svg?raw'
import sksEmptyIconSvg from '@/assets/inline-svg/sks-empty-icon.svg?raw'
import sksBlockedIconSvg from '@/assets/inline-svg/sks-blocked-icon.svg?raw'
import sksSyncIconSvg from '@/assets/inline-svg/sks-sync-icon.svg?raw'

const confirm = (value: boolean) => {
  closeSwitchKeySyncDialog(value)
}
</script>

<style scoped lang="less">
.sks-wrap {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  min-height: 100%;
  pointer-events: none;
}

.sks-card {
  pointer-events: auto;
  background: var(--color-white);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: var(--radius-l);
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
  width: 420px;
  max-width: 90vw;
  padding: 28px 28px 24px;
  display: flex;
  flex-direction: column;
  gap: 0;
}

.sks-title {
  font-size: 16px;
  font-weight: 700;
  color: var(--color-dark);
  text-align: center;
  margin-bottom: 6px;
}

.sks-subtitle {
  font-size: 12px;
  color: var(--color-gray-2);
  text-align: center;
  margin-bottom: 20px;
  line-height: 1.5;
}

/* ---- 图解区 ---- */
.sks-diagram {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 22px;
}

.diagram-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-radius: var(--radius-m, 10px);
  border: 1px solid transparent;

  &.no-sync {
    background: rgba(229, 62, 62, 0.04);
    border-color: rgba(229, 62, 62, 0.15);
  }

  &.synced {
    background: rgba(56, 161, 105, 0.04);
    border-color: rgba(56, 161, 105, 0.15);
  }
}

.preset-box {
  flex: 0 0 90px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;

  .preset-label {
    font-size: 11px;
    font-weight: 600;
    color: var(--brand-color-pure);
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }

  .key-chip {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 3px 8px;
    border-radius: 20px;
    font-size: 11px;
    font-weight: 500;
    background: rgba(var(--color-brand-1-rgb), 0.1);
    color: var(--brand-color-pure);
    white-space: nowrap;

    &.empty {
      background: rgba(217, 119, 6, 0.1);
      color: #d97706;
    }

    &.synced-chip {
      background: rgba(56, 161, 105, 0.12);
      color: #38a169;
    }
  }
}

/* ---- 箭头列 ---- */
.arrows-col {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.arrow-line {
  width: 100%;
}

.arrow-svg {
  width: 100%;
  height: 20px;
  overflow: visible;
}

.blocked-hint,
.synced-hint {
  display: flex;
  align-items: center;
  gap: 3px;
  font-size: 10px;
  font-weight: 500;
}

.blocked-hint {
  color: #e53e3e;
}

.synced-hint {
  color: #38a169;
}

/* ---- 虚线动画（不同步场景）---- */
.dash-anim {
  animation: dashMove 1.6s linear infinite;
}
@keyframes dashMove {
  from {
    stroke-dashoffset: 0;
  }
  to {
    stroke-dashoffset: -24;
  }
}

/* ---- 实线淡入动画（同步场景）---- */
.solid-anim {
  stroke-dasharray: 60;
  stroke-dashoffset: 60;
  animation: solidIn 0.6s ease-out 0.1s forwards;
}
.solid-anim-rev {
  stroke-dasharray: 60;
  stroke-dashoffset: 60;
  animation: solidIn 0.6s ease-out 0.35s forwards;
}
@keyframes solidIn {
  to {
    stroke-dashoffset: 0;
  }
}

/* ---- 按钮 ---- */
.sks-actions {
  display: flex;
  gap: 12px;
}

.sks-btn {
  flex: 1;
  height: 40px;
  border: none;
  border-radius: var(--action-btn-radius);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: var(--transition-base);

  &.primary {
    background: var(--brand-color);
    color: #fff;
    box-shadow: var(--action-btn-primary-shadow);

    &:hover {
      transform: translateY(-2px);
      box-shadow: var(--action-btn-primary-shadow-hover);
    }
  }

  &.secondary {
    background: var(--color-gray-1);
    color: var(--color-dark);

    &:hover {
      background: var(--color-gray-1);
      transform: translateY(-2px);
    }
  }

  &:active {
    transform: translateY(0);
  }
}
</style>
